import { type Currency, type FundingRound, type Headcount } from "@prisma/client";
import { value } from "~/components/helpers";
import { type AppContext } from "~/lib/context";
import { chain, compact, uniq, uniqBy } from "~/lib/lodash";
import { type GetMarketDataStatsResponse, type Input } from "~/pages/api/get-market-data-stats";
import { type NullableAuthenticatedUser } from "~/services/auth/fetchAuthenticatedUser";
import { fetchBenchmarkLevelsRange } from "~/services/benchmark-level/fetchBenchmarkLevelsRange";
import { getDefaultCurrency, getSuggestedCurrencies } from "~/services/currency";
import { getAllowedLevels, getMergedLevels, MA0X_LEVELS } from "~/services/employee/employeeLevel";
import { type GetLiveEmployeesOptions } from "~/services/employee/getLiveEmployees";
import { fetchExternalEmployeeIdsForBusinessUnits } from "~/services/external-employee/fetchExternalEmployeeIdsForBusinessUnits";
import { fetchUserExternalEmployeeManageeIdsIgnoringSuperAdmin } from "~/services/external-employee/fetchExternalEmployeeManageeIds";
import { computeMarketDataByLevel } from "~/services/market-data/computeMarketDataByLevel";
import { formatInputToMarketDataInput } from "~/services/market-data/formatInputToMarketDataInput";
import { getMarketDataEmployees } from "~/services/market-data/getMarketDataEmployees";

export const COMPANY_ID_FOR_ANONYMISED_DATA_IN_PRODUCTION = 14;
export const COMPANY_NAME_FOR_ANONYMISED_DATA_IN_TEST = "TestCompany";

type NumberRange = {
  min: number | null;
  max: number | null;
};

export type GetMarketDataStatsEmployeesCompanyTagsOptions = {
  tagIds: number[];
};

export type GetMarketDataStatsEmployeesOptions = GetLiveEmployeesOptions & {
  companySize?: NumberRange;
  externalEmployeeIds?: number[];
  funding?: NumberRange & { currency: Currency };
  fundingRounds?: FundingRound[];
  growthRates?: { max?: number; min?: number }[];
  headcount?: Headcount | null;
  industries?: GetMarketDataStatsEmployeesCompanyTagsOptions;
  labels?: GetMarketDataStatsEmployeesCompanyTagsOptions;
  omitCompanyId?: number;
  useTestData?: boolean;
  useBenchmarkLevelsInShare?: boolean;
  mergeAdvancedLevels?: boolean;
};

//Dataset Employee doesn't include 'externalEmployeeIds' as the 'You vs Market' feature should use the Employees rather than Dataset Employees
export type GetMarketDataStatsDatasetEmployeesOptions = Omit<GetMarketDataStatsEmployeesOptions, "externalEmployeeIds">;

export const getMarketDataStats = async (
  ctx: AppContext,
  input: Input,
  user: NullableAuthenticatedUser
): Promise<GetMarketDataStatsResponse> => {
  const company = await value(async () => {
    if (input.useTestData) {
      return ctx.prisma.company.findFirstOrThrow({
        where: {
          OR: [
            { id: COMPANY_ID_FOR_ANONYMISED_DATA_IN_PRODUCTION },
            { name: COMPANY_NAME_FOR_ANONYMISED_DATA_IN_TEST },
          ],
        },
      });
    }

    return user?.company ?? null;
  });

  const [currency, suggestedCurrenciesForLocations] = await Promise.all([
    input.currency
      ? ctx.prisma.currency.findUniqueOrThrow({ where: { code: input.currency } })
      : getDefaultCurrency(ctx),
    getSuggestedCurrencies(ctx, [...input.market.locationsIds, ...(input.compare?.locationsIds ?? [])]),
  ]);

  const suggestedCurrencies = uniqBy([currency, ...suggestedCurrenciesForLocations], (currency) => currency.code);

  //handling levels

  //to be removed when full live on Level Frameworks
  const allowedLevels = getAllowedLevels(user, input.mergeAdvancedLevels);

  let levelOptions = chain(input.levels)
    .flatMap((level) => getMergedLevels(level, { mergeAdvancedLevels: input.mergeAdvancedLevels }))
    .filter((level) => level !== "MA0X")
    .uniq()
    .value();

  if (input.levels.includes("MA0X")) {
    levelOptions = compact([...levelOptions, ...MA0X_LEVELS]);
  }

  const benchmarkLevelsRange = await fetchBenchmarkLevelsRange(ctx, {
    benchmarkLevelIds: input.benchmarkLevelIds,
    useBenchmarkLevelsInShare: input.useBenchmarkLevelsInShare ?? false,
  });

  const { marketWeightedJobs, marketJobsIds, employeesWithFiltersOptions } = await formatInputToMarketDataInput(ctx, {
    market: input.market,
    levels: levelOptions,
    benchmarkLevelsRange,
    useTestData: input.useTestData,
    mergeAdvancedLevels: input.mergeAdvancedLevels,
    canAccessFiguresAiV2: ctx.featureFlags.CAN_ACCESS_FIGURES_AI_V2,
    useBenchmarkLevelsInShare: input.useBenchmarkLevelsInShare ?? false,
  });

  const [companyEmployees, employeesWithFilters] = await Promise.all([
    value(async () => {
      if (!input.showCompanyEmployees || !ctx.globalPermissionsContext.canAccessYouVsMarket) {
        return null;
      }

      const externalEmployeeManageeIds = await fetchUserExternalEmployeeManageeIdsIgnoringSuperAdmin(ctx, { user });
      const externalEmployeeIdsInSameBusinessUnit = await fetchExternalEmployeeIdsForBusinessUnits(ctx);
      let externalEmployeeIds = null;

      if (externalEmployeeManageeIds || externalEmployeeIdsInSameBusinessUnit) {
        externalEmployeeIds = uniq(
          compact([...(externalEmployeeManageeIds ?? []), ...(externalEmployeeIdsInSameBusinessUnit ?? [])])
        );
      }

      //company employees
      return getMarketDataEmployees(ctx, {
        useTestData: input.useTestData,
        jobIds: marketJobsIds,
        levels: levelOptions,
        benchmarkLevelsRange: benchmarkLevelsRange,
        useBenchmarkLevelsInShare: input.useBenchmarkLevelsInShare ?? false,
        locationIds: input.market.locationsIds,
        companyIds: company ? [company.id] : undefined,
        ...(externalEmployeeIds && { externalEmployeeIds }),
        mergeAdvancedLevels: input.mergeAdvancedLevels,
      });
    }),
    // employees with filters for market
    getMarketDataEmployees(ctx, employeesWithFiltersOptions),
  ]);

  const marketIndustryTag = input.market.industryId
    ? await ctx.prisma.companyTag.findUnique({ where: { id: input.market.industryId } })
    : null;

  const marketDataByLevel = await computeMarketDataByLevel(ctx, {
    levels: input.levels,
    benchmarkLevelsRange,
    useBenchmarkLevelsInShare: input.useBenchmarkLevelsInShare ?? false,
    locationIds: input.market.locationsIds,
    allowedLevels,
    employees: employeesWithFilters,
    currency,
    weightedJobs: marketWeightedJobs,
    percentiles: input.percentiles,
    company: company ?? undefined,
    headcount: input.market.headcount,
    industry: marketIndustryTag,
    fundingRounds: input.market.fundingRounds,
    mergeAdvancedLevels: input.mergeAdvancedLevels,
    isForCompany: input.showCompanyEmployees,
  });

  const stats: GetMarketDataStatsResponse = {
    currency,
    suggestedCurrencies,
    percentiles: input.percentiles,
    market: {
      byLevel: marketDataByLevel,
    },
  };

  if (input.showCompanyEmployees && companyEmployees) {
    companyEmployees.forEach((employee) => {
      if (!ctx.featureFlags.CAN_ACCESS_LEVEL_FRAMEWORKS) {
        stats.market.byLevel[employee.level]?.companyEmployees.push(employee);
      }

      if (ctx.featureFlags.CAN_ACCESS_LEVEL_FRAMEWORKS) {
        stats.market.byLevel[employee.benchmarkLevel?.id]?.companyEmployees.push(employee);
      }
    });
  }

  //Compare to a second job function
  if (input.compare) {
    const {
      marketWeightedJobs: compareWeightedJobs,
      marketJobsIds: compareJobsIds,
      employeesWithFiltersOptions: comparedEmployeesWithFiltersOptions,
    } = await formatInputToMarketDataInput(ctx, {
      market: input.compare,
      levels: levelOptions,
      benchmarkLevelsRange,
      useTestData: input.useTestData,
      mergeAdvancedLevels: input.mergeAdvancedLevels,
      canAccessFiguresAiV2: ctx.featureFlags.CAN_ACCESS_FIGURES_AI_V2,
    });

    const [comparedCompanyEmployees, comparedEmployeesWithFilters] = await Promise.all([
      value(async () => {
        if (!input.showCompanyEmployees || !ctx.globalPermissionsContext.canAccessYouVsMarket) {
          return null;
        }

        const externalEmployeeManageeIds = await fetchUserExternalEmployeeManageeIdsIgnoringSuperAdmin(ctx, { user });
        const externalEmployeeIdsInSameBusinessUnit = await fetchExternalEmployeeIdsForBusinessUnits(ctx);
        let externalEmployeeIds = null;

        if (externalEmployeeManageeIds || externalEmployeeIdsInSameBusinessUnit) {
          externalEmployeeIds = uniq(
            compact([...(externalEmployeeManageeIds ?? []), ...(externalEmployeeIdsInSameBusinessUnit ?? [])])
          );
        }

        // compared company employees
        return getMarketDataEmployees(ctx, {
          useTestData: input.useTestData,
          jobIds: compareJobsIds ?? [],
          levels: levelOptions,
          companyIds: company ? [company.id] : undefined,
          locationIds: input.compare?.locationsIds,
          ...(externalEmployeeIds && { externalEmployeeIds }),
          mergeAdvancedLevels: input.mergeAdvancedLevels,
        });
      }),
      value(() => {
        if (!input.compare || comparedEmployeesWithFiltersOptions === null) {
          return null;
        }

        return getMarketDataEmployees(ctx, comparedEmployeesWithFiltersOptions);
      }),
    ]);

    if (input.compare && comparedEmployeesWithFilters && compareWeightedJobs && input.compare.jobsIds.length) {
      const compareIndustryTag = input.compare.industryId
        ? await ctx.prisma.companyTag.findUnique({ where: { id: input.compare.industryId } })
        : null;

      stats.comparison = {
        byLevel: await computeMarketDataByLevel(ctx, {
          levels: input.levels,
          benchmarkLevelsRange,
          useBenchmarkLevelsInShare: input.useBenchmarkLevelsInShare ?? false,
          locationIds: input.compare.locationsIds,
          allowedLevels,
          employees: comparedEmployeesWithFilters,
          currency,
          weightedJobs: compareWeightedJobs,
          percentiles: input.percentiles,
          company: company ?? undefined,
          headcount: input.compare.headcount,
          industry: compareIndustryTag,
          fundingRounds: input.compare.fundingRounds,
          mergeAdvancedLevels: input.mergeAdvancedLevels,
        }),
      };
    }

    if (input.showCompanyEmployees && comparedCompanyEmployees) {
      comparedCompanyEmployees.forEach((employee) => {
        if (!ctx.featureFlags.CAN_ACCESS_LEVEL_FRAMEWORKS) {
          stats.market.byLevel[employee.level]?.companyEmployees.push(employee);
        }

        if (ctx.featureFlags.CAN_ACCESS_LEVEL_FRAMEWORKS) {
          stats.market.byLevel[employee.benchmarkLevel?.id]?.companyEmployees.push(employee);
        }
      });
    }
  }

  return stats;
};
